import type { SxProps, Theme } from '@mui/material'
import { banhangKiotLike } from '@/components/layout/banhangKiotTokens'

export type BranchPickerTone = 'theme' | 'light'

type ThemeColors = {
  paper: string
  item: string
  itemHover: string
  text: string
  activeBg: string
  activeText: string
  divider: string
}

/**
 * Màu menu đổi CN header (theme banhang).
 * Nền list sáng hơn một chút; item tối hơn rõ để không lẫn.
 */
export function branchPickerThemeColors(input: {
  submenuBg: string
  contrastText: string
  primaryMain: string
  paperBg: string
  darken: (color: string, coefficient: number) => string
}): ThemeColors {
  const { submenuBg, contrastText, primaryMain, paperBg, darken } = input
  return {
    paper: darken(submenuBg, 0.12),
    item: darken(submenuBg, 0.32),
    itemHover: darken(submenuBg, 0.2),
    text: contrastText,
    activeBg: paperBg,
    activeText: primaryMain,
    divider: 'rgba(255, 255, 255, 0.2)',
  }
}

/** Modal: nền list xám nhạt, item trắng nổi lên. */
const lightColors: ThemeColors = {
  paper: '#f3f5f8',
  item: '#fff',
  itemHover: '#e8edf3',
  text: banhangKiotLike.kiotTextPrimary,
  activeBg: 'rgba(0, 90, 195, 0.12)',
  activeText: '#005ac3',
  divider: '#e1e6ed',
}

export function branchPickerPaperSx(
  tone: BranchPickerTone,
  colors: ThemeColors,
  width?: number | string,
): SxProps<Theme> {
  const w = width ?? 'min(300px, calc(100vw - 16px))'
  return {
    mt: 0.5,
    py: 0.5,
    px: 0.5,
    width: w,
    minWidth: w,
    maxWidth: 'min(360px, calc(100vw - 16px))',
    maxHeight: 420,
    overflowY: 'auto',
    boxSizing: 'border-box',
    bgcolor: colors.paper,
    color: colors.text,
    borderRadius: `${banhangKiotLike.kiotSubmenuPaperRadiusPx}px`,
    boxShadow:
      tone === 'theme'
        ? '0 8px 20px rgba(0, 0, 0, 0.22)'
        : '0 10px 28px rgba(15, 23, 42, 0.12)',
    border: tone === 'light' ? `1px solid ${lightColors.divider}` : 'none',
    backgroundImage: 'none',
  }
}

export const branchPickerListSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  py: 0,
  px: 0,
  width: '100%',
}

/** Khối mảng: không box border — chỉ đường ngang ngăn với mảng trước. */
export function branchPickerGroupSx(colors: ThemeColors, isFirst: boolean): SxProps<Theme> {
  return {
    px: 0.25,
    pt: isFirst ? 0 : 0.5,
    pb: 0.35,
    mt: 0,
    borderTop: isFirst ? 'none' : `1px solid ${colors.divider}`,
    borderRadius: 0,
    bgcolor: 'transparent',
  }
}

export function branchPickerGroupTitleSx(colors: ThemeColors): SxProps<Theme> {
  return {
    px: 0.25,
    pb: 0.25,
    mb: 0.25,
    fontFamily: banhangKiotLike.kiotFontStack,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0,
    lineHeight: '15px',
    color: colors.text,
    borderBottom: 'none',
    textTransform: 'none',
  }
}

export const branchPickerGridSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '2px',
}

/** Nút CN — cao hơn, khoảng cách item/mảng gọn. */
export function branchPickerItemSx(
  tone: BranchPickerTone,
  colors: ThemeColors,
  active: boolean,
  opts?: { disabled?: boolean; fullWidth?: boolean },
): SxProps<Theme> {
  const disabled = opts?.disabled
  if (tone === 'theme') {
    return {
      m: 0,
      px: 0.75,
      height: 30,
      minWidth: 0,
      width: opts?.fullWidth ? '100%' : undefined,
      textAlign: 'left',
      borderRadius: '4px',
      border: active
        ? `1px solid ${colors.activeBg}`
        : '1px solid rgba(255, 255, 255, 0.14)',
      bgcolor: active ? colors.activeBg : colors.item,
      boxShadow: 'none',
      color: active ? colors.activeText : colors.text,
      fontFamily: banhangKiotLike.kiotFontStack,
      fontSize: '12px',
      fontWeight: active ? 700 : 400,
      lineHeight: '28px',
      cursor: disabled ? 'wait' : 'pointer',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      opacity: disabled ? 0.6 : 1,
      '&:hover': {
        bgcolor: active ? colors.activeBg : colors.itemHover,
      },
      '&:disabled': { opacity: 0.6 },
    }
  }

  return {
    m: 0,
    px: 1,
    height: 34,
    minWidth: 0,
    width: opts?.fullWidth ? '100%' : undefined,
    textAlign: 'left',
    borderRadius: '4px',
    border: active
      ? `1px solid ${colors.activeText}`
      : `1px solid ${lightColors.divider}`,
    bgcolor: active ? colors.activeBg : colors.item,
    boxShadow: 'none',
    color: active ? colors.activeText : colors.text,
    fontFamily: banhangKiotLike.kiotFontStack,
    fontSize: 13,
    fontWeight: active ? 700 : 500,
    lineHeight: '32px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    opacity: disabled ? 0.55 : 1,
    '&:hover': disabled
      ? undefined
      : {
          bgcolor: active ? colors.activeBg : colors.itemHover,
        },
    '&:disabled': { opacity: 0.55 },
  }
}

export { lightColors as branchPickerLightColors }
